import { WS_RELIABLE, WS_UNRELIABLE, encodeWsFrame } from "./framing";
import type { Transport } from "./transport";

/** The part of `WebSocket` the transport uses (lets tests inject a fake). */
export interface WebSocketLike {
  binaryType: string;
  readonly readyState: number;
  onopen: ((ev: unknown) => void) | null;
  onmessage: ((ev: { data: unknown }) => void) | null;
  onclose: ((ev: { code: number; reason: string }) => void) | null;
  onerror: ((ev: unknown) => void) | null;
  /** Bytes passed to `send` that have not reached the network yet. */
  readonly bufferedAmount?: number;
  send(data: Uint8Array<ArrayBuffer>): void;
  close(code?: number, reason?: string): void;
}
export type WebSocketFactory = (url: string) => WebSocketLike;

const defaultWebSocketFactory: WebSocketFactory = (url) => {
  // WebSocketLike types the event handlers more loosely than the DOM does, so tests can fake it.
  const ws: unknown = new WebSocket(url);
  return ws as WebSocketLike;
};

const OPEN = 1;
/**
 * Unreliable messages are not queued behind more than this many unsent bytes (about 20 input
 * packets): on a stalled connection TCP would deliver them all, late, in one burst, and only the
 * newest are worth sending.
 */
const MAX_BUFFERED_UNRELIABLE = 2048;

/**
 * WebSocket transport: every message is one binary frame `[channel][payload]` with channel 0x00
 * for unreliable and 0x01 for reliable messages.
 */
export class WebSocketTransport implements Transport {
  readonly kind = "websocket" as const;
  readonly ready: Promise<void>;
  onReliable: ((payload: Uint8Array) => void) | null = null;
  onUnreliable: ((payload: Uint8Array) => void) | null = null;
  onClose: ((reason: string) => void) | null = null;

  private readonly ws: WebSocketLike;
  private opened = false;
  private finished = false;

  constructor(url: string, create: WebSocketFactory = defaultWebSocketFactory) {
    const ws = create(url);
    ws.binaryType = "arraybuffer";
    this.ws = ws;
    const { promise, resolve, reject } = Promise.withResolvers<void>();
    this.ready = promise;
    // A failed handshake is reported through `ready`; never leave it unhandled.
    promise.catch(() => undefined);
    ws.onopen = () => {
      this.opened = true;
      resolve();
    };
    ws.onerror = () => {
      // The close event that follows carries the details; an error before open fails the handshake.
      if (!this.opened) reject(new Error("WebSocket connection failed"));
    };
    ws.onclose = (ev) => {
      const reason = ev.reason || `WebSocket closed (code ${ev.code})`;
      if (!this.opened) reject(new Error(reason));
      this.finish(reason);
    };
    ws.onmessage = (ev) => this.receive(ev.data);
  }

  private receive(data: unknown): void {
    if (this.finished || !(data instanceof ArrayBuffer) || data.byteLength < 2) return;
    const bytes = new Uint8Array(data);
    const payload = bytes.subarray(1);
    if (bytes[0] === WS_UNRELIABLE) this.onUnreliable?.(payload);
    else if (bytes[0] === WS_RELIABLE) this.onReliable?.(payload);
  }

  private finish(reason: string): void {
    if (this.finished) return;
    this.finished = true;
    this.onClose?.(reason);
  }

  sendReliable(payload: Uint8Array): void {
    this.send(WS_RELIABLE, payload);
  }

  sendUnreliable(payload: Uint8Array): void {
    if ((this.ws.bufferedAmount ?? 0) > MAX_BUFFERED_UNRELIABLE) return;
    this.send(WS_UNRELIABLE, payload);
  }

  private send(channel: number, payload: Uint8Array): void {
    if (this.finished || this.ws.readyState !== OPEN) return;
    this.ws.send(encodeWsFrame(channel, payload));
  }

  close(): void {
    this.finished = true;
    this.ws.onmessage = null;
    try {
      this.ws.close(1000);
    } catch {
      // Already closed.
    }
  }
}
